'use client'

import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Button from '@/components/ui/button'
import Alert from '@/components/ui/alert'
import Checkbox from '@/components/ui/checkbox'
import DatePicker from 'react-datepicker'
import Input from '@/components/ui/input'
import Textarea from '@/components/ui/textarea'
import Label from '@/components/ui/label'
import { createMeetup, updateMeetup } from '@/services/meetups'
import { uploadImage, deleteImage } from '@/services/storage'
import 'react-datepicker/dist/react-datepicker.css'
import '@/styles/datepicker-custom.css'
import { meetupSchema, MeetupFormValues } from './schemas'

interface MeetupFormProps {
  initialData?: any
  userId: string
  onSuccess?: (data: any) => void
  onCancel?: () => void
}

function MeetupForm({ initialData, userId, onSuccess, onCancel }: MeetupFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(
    initialData?.cover_image_url || null
  )
  const [isRemovedImage, setIsRemovedImage] = useState(false)
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const [signupTooltipVisible, setSignupTooltipVisible] = useState(false)
  const [saveAsDraft, setSaveAsDraft] = useState(initialData?.status === 'draft')
  const [isMultiDay, setIsMultiDay] = useState(false)
  const [startTimeObj, setStartTimeObj] = useState<Date | null>(null)
  const [endTimeObj, setEndTimeObj] = useState<Date | null>(null)
  const [enableSignupStartTime, setEnableSignupStartTime] = useState(
    !!initialData?.signup_start_time
  )

  // Initialize form
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<MeetupFormValues>({
    resolver: zodResolver(meetupSchema),
    defaultValues: initialData
      ? {
          ...initialData,
          start_time: initialData.start_time ? new Date(initialData.start_time) : undefined,
          end_time: initialData.end_time ? new Date(initialData.end_time) : undefined,
          signup_start_time: initialData.signup_start_time
            ? new Date(initialData.signup_start_time)
            : undefined,
          add_placeholders: initialData.add_placeholders !== false,
        }
      : {
          title: '',
          description: '',
          location: '',
          address: '',
          is_private: false,
          add_placeholders: true,
          status: 'draft',
          start_time: new Date(new Date().setHours(12, 0, 0, 0)),
          end_time: new Date(new Date().setHours(14, 0, 0, 0)),
        },
  })

  // Watch for date changes to determine if the event is multi-day
  const startDate = watch('start_time')
  const endDate = watch('end_time')

  // Check if the event spans multiple days
  useEffect(() => {
    if (startDate && endDate) {
      // Reset both dates to midnight to compare just the date portion
      const start = new Date(startDate)
      start.setHours(0, 0, 0, 0)

      const end = new Date(endDate)
      end.setHours(0, 0, 0, 0)

      // Calculate the difference in days
      const timeDiff = end.getTime() - start.getTime()
      const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24))

      setIsMultiDay(daysDiff > 0)

      // If not multi-day, uncheck the limit_time_range checkbox
      if (daysDiff === 0) {
        setValue('limit_time_range', false)
      }
    }
  }, [startDate, endDate, setValue])

  // Set up default time objects for the time pickers
  useEffect(() => {
    // Create dates with default times
    const defaultStartTime = new Date()
    defaultStartTime.setHours(10, 0, 0, 0)

    const defaultEndTime = new Date()
    defaultEndTime.setHours(22, 0, 0, 0)

    // If we have existing values, use those
    if (initialData?.daily_start_time) {
      const [hours, minutes] = initialData.daily_start_time.split(':').map(Number)
      const startTime = new Date()
      startTime.setHours(hours, minutes, 0, 0)
      setStartTimeObj(startTime)
    } else {
      setStartTimeObj(defaultStartTime)
      setValue('daily_start_time', '10:00')
    }

    if (initialData?.daily_end_time) {
      const [hours, minutes] = initialData.daily_end_time.split(':').map(Number)
      const endTime = new Date()
      endTime.setHours(hours, minutes, 0, 0)
      setEndTimeObj(endTime)
    } else {
      setEndTimeObj(defaultEndTime)
      setValue('daily_end_time', '22:00')
    }
  }, [initialData, setValue])

  // Format time object to HH:MM string
  const formatTimeToString = (date: Date | null): string => {
    if (!date) return ''
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${hours}:${minutes}`
  }

  // Handle image change
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setImageFile(file)
    setIsRemovedImage(false)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  // Handle remove image
  const handleRemoveImage = () => {
    setImageFile(null)
    setImagePreview(null)
    setIsRemovedImage(true)
  }

  // Handle form submission
  const onSubmit = async (data: MeetupFormValues, saveDraft = false) => {
    if (!userId) {
      setError('You must be logged in to create a meetup')
      return
    }

    try {
      setIsSubmitting(true)
      setError(null)

      let coverImageUrl = initialData?.cover_image_url

      // Handle image upload/deletion
      if (imageFile) {
        // Upload new image with user ID
        const uploadResult = await uploadImage(imageFile, 'meetups', userId)
        if (uploadResult.error) {
          throw new Error(uploadResult.error)
        }
        coverImageUrl = uploadResult.url

        // Delete old image if exists
        if (initialData?.cover_image_url) {
          await deleteImage(initialData.cover_image_url)
        }
      } else if (isRemovedImage && initialData?.cover_image_url) {
        // Delete image without replacement
        await deleteImage(initialData.cover_image_url)
        coverImageUrl = null
      }

      // Prepare meetup data
      const meetupData = {
        ...data,
        start_time: data.start_time.toISOString(),
        end_time: data.end_time.toISOString(),
        signup_start_time:
          enableSignupStartTime && data.signup_start_time
            ? data.signup_start_time.toISOString()
            : null,
        host_id: userId,
        cover_image_url: coverImageUrl,
        status: saveDraft ? 'draft' : 'published',
        limit_time_range: isMultiDay ? data.limit_time_range : false,
        daily_start_time: isMultiDay && data.limit_time_range ? data.daily_start_time : null,
        daily_end_time: isMultiDay && data.limit_time_range ? data.daily_end_time : null,
      }

      // Only include add_placeholders for new meetups
      if (!initialData) {
        meetupData.add_placeholders = data.add_placeholders
      }

      // Create or update meetup
      const result = initialData
        ? await updateMeetup(initialData.id, meetupData)
        : await createMeetup(meetupData)

      if (result.error) {
        throw new Error(result.error)
      }

      // Call success callback
      if (onSuccess) {
        onSuccess(result.data)
      }

      // Reset form state after successful submission
      setImageFile(null)
      setImagePreview(null)
    } catch (err) {
      console.error('Error submitting meetup form:', err)
      setError(err instanceof Error ? err.message : 'Failed to save meetup')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle save as draft button click
  const handleSaveAsDraft = async () => {
    handleSubmit(data => onSubmit(data, true))()
  }

  return (
    <form onSubmit={handleSubmit(data => onSubmit(data, false))} className="space-y-6">
      {error && (
        <Alert variant="error" title="Error">
          {error}
        </Alert>
      )}

      <div className="space-y-4">
        {/* Cover Image */}
        <div>
          <Label htmlFor="cover_image">Cover Image</Label>
          <div className="mt-1 flex items-center gap-4">
            <div className="relative w-32 h-32 border border-gray-700 rounded-md overflow-hidden">
              {imagePreview ? (
                <>
                  <img
                    src={imagePreview}
                    alt="Cover preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                    aria-label="Remove image"
                  >
                    ×
                  </button>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-800 text-gray-400">
                  No image
                </div>
              )}
            </div>
            <div>
              <input
                id="cover_image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('cover_image')?.click()}
              >
                {imagePreview ? 'Change Image' : 'Upload Image'}
              </Button>
              <p className="mt-1 text-sm text-gray-400">Recommended size: 1200 × 630 pixels</p>
            </div>
          </div>
        </div>

        {/* Title */}
        <div>
          <Label htmlFor="title" required>
            Title
          </Label>
          <Input
            id="title"
            {...register('title')}
            error={errors.title?.message}
            placeholder="Meetup title"
          />
        </div>

        {/* Description */}
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            {...register('description')}
            error={errors.description?.message}
            placeholder="Describe your meetup"
            rows={4}
          />
        </div>

        {/* Location */}
        <div>
          <Label htmlFor="location">Location Name</Label>
          <Input
            id="location"
            {...register('location')}
            error={errors.location?.message}
            placeholder="e.g. Central Park, My House, etc."
          />
        </div>

        {/* Address */}
        <div>
          <Label htmlFor="address">Address</Label>
          <Input
            id="address"
            {...register('address')}
            error={errors.address?.message}
            placeholder="Full address"
          />
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="start_time" required>
              Start Date & Time
            </Label>
            <Controller
              name="start_time"
              control={control}
              render={({ field }) => (
                <DatePicker
                  id="start_time"
                  selected={field.value}
                  onChange={(date: Date | null) => {
                    field.onChange(date)
                    setStartTimeObj(date)

                    // If signup time is not set, default to current date
                    if (enableSignupStartTime && !watch('signup_start_time')) {
                      setValue('signup_start_time', new Date())
                    }
                  }}
                  showTimeSelect
                  timeFormat="p"
                  timeIntervals={30}
                  dateFormat="MM/dd/yyyy h:mm aa"
                  className="w-full p-2 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-primary focus:ring-primary"
                  placeholderText="Select start date and time"
                />
              )}
            />
            {errors.start_time && (
              <p className="text-sm text-red-500">{String(errors.start_time.message)}</p>
            )}
          </div>

          {/* Add signup start time input */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enable_signup_time"
                checked={enableSignupStartTime}
                onChange={e => {
                  const checked = e.target.checked
                  setEnableSignupStartTime(checked)
                  if (checked && !watch('signup_start_time')) {
                    setValue('signup_start_time', new Date())
                  }
                }}
              />
              <div className="flex items-center">
                <Label htmlFor="enable_signup_time">Set RSVP start time</Label>
                <div className="ml-2 relative">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-white focus:outline-none"
                    onMouseEnter={() => setSignupTooltipVisible(true)}
                    onMouseLeave={() => setSignupTooltipVisible(false)}
                    onClick={e => {
                      e.preventDefault()
                      setSignupTooltipVisible(!signupTooltipVisible)
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {signupTooltipVisible && (
                    <div className="absolute left-6 bottom-0 z-10 w-64 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md shadow-lg">
                      <p className="text-sm text-gray-200">
                        When set, attendees can only RSVP after this time. Until then, RSVP buttons
                        will be disabled for all events in this meetup.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {enableSignupStartTime && (
              <>
                <Label htmlFor="signup_start_time">RSVP Start Date & Time</Label>
                <Controller
                  name="signup_start_time"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      id="signup_start_time"
                      selected={field.value}
                      onChange={field.onChange}
                      showTimeSelect
                      timeFormat="p"
                      timeIntervals={30}
                      dateFormat="MM/dd/yyyy h:mm aa"
                      className="w-full p-2 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-primary focus:ring-primary"
                      placeholderText="Select RSVP start date and time"
                    />
                  )}
                />
                {errors.signup_start_time && (
                  <p className="text-sm text-red-500">{String(errors.signup_start_time.message)}</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* End Date & Time */}
        <div>
          <Label htmlFor="end_time" required>
            End Date & Time
          </Label>
          <Controller
            name="end_time"
            control={control}
            render={({ field }) => (
              <DatePicker
                id="end_time"
                selected={field.value}
                onChange={field.onChange}
                showTimeSelect
                timeFormat="p"
                timeIntervals={30}
                dateFormat="MM/dd/yyyy h:mm aa"
                className="w-full p-2 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-primary focus:ring-primary"
                placeholderText="Select end date and time"
                minDate={control._formValues.start_time}
              />
            )}
          />
          {errors.end_time && (
            <p className="text-sm text-red-500">{String(errors.end_time.message)}</p>
          )}
        </div>

        {/* Is Private */}
        <div className="flex items-center space-x-2">
          <Controller
            name="is_private"
            control={control}
            render={({ field }) => (
              <Checkbox id="is_private" checked={field.value} onChange={field.onChange} />
            )}
          />
          <Label htmlFor="is_private">Private Meetup</Label>
        </div>

        {/* Time Range Limits for Multi-day Events - only show if multi-day */}
        {isMultiDay && (
          <div className="mt-4">
            <div className="flex items-center space-x-2">
              <Controller
                name="limit_time_range"
                control={control}
                render={({ field }) => (
                  <Checkbox id="limit_time_range" checked={field.value} onChange={field.onChange} />
                )}
              />
              <Label htmlFor="limit_time_range">Set daily time range for multi-day events</Label>
            </div>

            {watch('limit_time_range') && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800 p-3 rounded-md">
                <div>
                  <Label htmlFor="daily_start_time">Daily Start Time</Label>
                  <Controller
                    name="daily_start_time"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        id="daily_start_time"
                        selected={startTimeObj}
                        onChange={(date: Date | null) => {
                          setStartTimeObj(date)
                          field.onChange(date ? formatTimeToString(date) : '')
                        }}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={30}
                        timeCaption="Start Time"
                        dateFormat="h:mm aa"
                        className="w-full p-2 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-primary focus:ring-primary"
                      />
                    )}
                  />
                </div>
                <div>
                  <Label htmlFor="daily_end_time">Daily End Time</Label>
                  <Controller
                    name="daily_end_time"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        id="daily_end_time"
                        selected={endTimeObj}
                        onChange={(date: Date | null) => {
                          setEndTimeObj(date)
                          field.onChange(date ? formatTimeToString(date) : '')
                        }}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={30}
                        timeCaption="End Time"
                        dateFormat="h:mm aa"
                        className="w-full p-2 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-primary focus:ring-primary"
                      />
                    )}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add Placeholders */}
        <div className="flex items-center space-x-2 relative">
          <Controller
            name="add_placeholders"
            control={control}
            render={({ field }) => (
              <Checkbox id="add_placeholders" checked={field.value} onChange={field.onChange} />
            )}
          />
          <div className="flex items-center">
            <Label htmlFor="add_placeholders">Add placeholders</Label>
            <div className="ml-2 relative">
              <button
                type="button"
                className="text-gray-400 hover:text-white focus:outline-none"
                onMouseEnter={() => setTooltipVisible(true)}
                onMouseLeave={() => setTooltipVisible(false)}
                onClick={e => {
                  e.preventDefault()
                  setTooltipVisible(!tooltipVisible)
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {tooltipVisible && (
                <div className="absolute left-6 bottom-0 z-10 w-64 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md shadow-lg">
                  <p className="text-sm text-gray-200">
                    This will add a one hour meeting to the start and end of your event for setup
                    and cleanup
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="button"
          variant="secondary"
          onClick={handleSaveAsDraft}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Draft'}
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Publish Meetup'}
        </Button>
      </div>
    </form>
  )
}

export default MeetupForm
